import { accountDb } from './firebase';
import { collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { UserPreference } from '../types';

// Collection in the Account Database (mindsprint-cfb30)
// We use 'sync_data' to store preferences directly on the user record for Portal integration.
const COLLECTION_NAME = 'sync_data';

export const userService = {
  /**
   * Retrieves the user's preferences from the Account Database (sync_data).
   */
  async getUserPreferences(userId: string): Promise<UserPreference | null> {
    if (!accountDb || userId === 'guest') return null;

    try {
      // Find the user document by username
      const q = query(collection(accountDb, COLLECTION_NAME), where('username', '==', userId));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        // Return the document data cast as UserPreference (ignoring other auth fields)
        return snapshot.docs[0].data() as UserPreference;
      }
      return null;
    } catch (e) {
      console.error("Error fetching user preferences from Account DB:", e);
      return null;
    }
  },

  /**
   * Updates the user's preferences in the Account Database (sync_data).
   */
  async updateUserPreferences(userId: string, prefs: Partial<UserPreference>): Promise<void> {
    if (!accountDb || userId === 'guest') return;

    try {
      const q = query(collection(accountDb, COLLECTION_NAME), where('username', '==', userId));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const docRef = snapshot.docs[0].ref;
        await updateDoc(docRef, {
          ...prefs,
          lastUpdated: Date.now()
        });
      } else {
        console.warn(`User ${userId} not found in ${COLLECTION_NAME} during preference update.`);
      }
    } catch (e) {
      console.error("Error updating user preferences in Account DB:", e);
    }
  },

  /**
   * Updates just the cached balance if the user has enabled display.
   * Optimized to perform one query to check flag and update.
   */
  async updateCachedBalance(userId: string, balance: number): Promise<void> {
    if (!accountDb || userId === 'guest') return;
    
    try {
        const q = query(collection(accountDb, COLLECTION_NAME), where('username', '==', userId));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
            const docSnap = snapshot.docs[0];
            const data = docSnap.data() as UserPreference;
            
            // Only update if the user has opted in
            if (data.displayTotalBalance) {
                await updateDoc(docSnap.ref, { 
                    cachedTotalBalance: balance,
                    lastUpdated: Date.now()
                });
            }
        }
    } catch(e) {
        console.error("Failed to update cached balance in Account DB", e);
    }
  }
};
